import React, { useRef } from "react";
import { useState, useEffect } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Typography,
  Grid,
  Avatar,
  CircularProgress,
  TextField,
  Autocomplete,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import {
  ApiGetShopperVenueMap,
  ApiGetShopperPopUpDetails,
  ApiPutShopperDetails,
  ApiGetVenuePreviousReports,
} from "../../../apiService/Core/Apicall.tsx";
import { toast } from "react-toastify";
import { InfoBox } from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "500px",
};

const center = {
  lat: -25.2744,
  lng: 133.7751,
};

// States options array
const stateOptions = [
  { label: "Victoria", value: "victoria" },
  { label: "Western Australia", value: "western_australia" },
  { label: "South Australia", value: "south_australia" },
  { label: "Queensland", value: "queensland" },
  { label: "Australian Capital Territory", value: "act" },
  { label: "New South Wales", value: "nsw" },
  { label: "Tasmania", value: "tasmania" },
  { label: "Northern Territory", value: "northern_territory" },
];

// Types for API response
interface VenueMapData {
  clientGroupName: string;
  entityTypeId: number;
  entityTypeName: string;
  clientEntityId: number;
  entityName: string;
  venueId: number;
  venueName: string;
  lat: string;
  lng: string;
  logo: string;
}

interface UserData {
  user_userId: number;
  user_firstName: string;
  user_lastName: string;
  user_contactNo: string;
  user_emailId: string;
  user_dob: string;
  user_latitude: string | null;
  user_longitude: string | null;
  user_userName: string;
  age: string | null;
  userStatusName: string;
}

interface ApiResponse {
  status: boolean;
  message: string;
  data: {
    venueMapDatas: VenueMapData[];
    userData: {
      status_filtered_users: UserData[];
    };
  };
}

// Interface for shopper details API response
interface ShopperDetails {
  contactDetails: {
    age: string;
    email: string;
    phone: string;
  };
  currentMonthAllocationCounts: {
    MM: number;
    BLOOMS: number;
    ALDI: number;
    TRANSPORT: number;
  };
  history: {
    totalReports: number;
    aldi: number;
    blooms: number;
    mm: number;
    beacon: number;
  };
  stats: {
    memberSince: string;
    lastReportRating: number;
    lastMonth: string;
    overallRating: string;
  };
  editorComment: string;
  shopperNotes: string[];
  trainingNotes: string;
}

// Marker interface for map
interface MapMarker {
  id: number;
  name: string;
  lat: number;
  lng: number;
  type: string;
  logo: string;
  entityName: string;
  age: any;
  clientGroupName: string;
  index: number;
  dob: any;
  emailId: string;
  contactNo: string;
  venuePriceDisplay?: string;
}

const ShopperVenueMap: React.FC = () => {
  const [selectedMarker, setSelectedMarker] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [loading, setLoading] = useState<boolean>(false);
  const [mapMarkers, setMapMarkers] = useState<MapMarker[]>([]);
  const [shopperMarkers, setShopperMarkers] = useState<MapMarker[]>([]);
  //const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState("");

  // State for shopper details
  const [shopperDetails, setShopperDetails] = useState<any>(null);
  const [loadingShopperDetails, setLoadingShopperDetails] =
    useState<boolean>(false);
  const [shopperNotes1, setShopperNotes1] = useState("");
  const [ShopperName, setShopperName] = useState("");
  const [trainingNotes, setTrainingNotes] = useState("");

  // State for venue details
  const [venueDetails, setVenueDetails] = useState<any>(null);
  const [loadingVenueDetails, setLoadingVenueDetails] =
    useState<boolean>(false);

  const [checkboxStates, setCheckboxStates] = useState({
    shopperActive18: false,
    shopperActiveOther: false,
    shopperInactive: false,
    showAldi: false,
    showBloom: false,
    showMM: false,
  });
  const [staticLineInserted, setStaticLineInserted] = useState(false);
  const [staticLineInserted2, setStaticLineInserted2] = useState(false);
  const shoppernoteRef = useRef<HTMLTextAreaElement | null>(null);
  const trainingnoteRef = useRef<HTMLTextAreaElement | null>(null);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyCmcI0swO1MNjQYUD7qktNT78-cDMBoi44",
  });

  // Function to call API
  const fetchMapData = async () => {
    try {
      setLoading(true);
      // Build parameters
      const paramsAttachment: any = [];

      // Add selected states if any are selected
      if (selectedState != "") {
        // Pass raw string, no encoding
        paramsAttachment.push(`state=${selectedState}`);
      } else {
        toast.error("Please select state");
        return;
      }

      // Add client groups if any are selected
      const selectedClientGroups: any = [];
      if (checkboxStates.showAldi) selectedClientGroups.push("ALDI");
      if (checkboxStates.showBloom) selectedClientGroups.push("BLOOMS");
      if (checkboxStates.showMM) selectedClientGroups.push("MM");

      const selectClientGroup = selectedClientGroups.join(","); // e.g., "ALDI,BLOOMS"
      if (selectClientGroup) {
        paramsAttachment.push(`ClientGroupName=${selectClientGroup}`);
      }

      const selectedShoppers: any = [];
      if (checkboxStates.shopperActive18) selectedShoppers.push("active");
      if (checkboxStates.shopperActiveOther) selectedShoppers.push("active");
      if (checkboxStates.shopperInactive) selectedShoppers.push("inActive");

      const selectedShopper = selectedShoppers.join(",");
      if (selectedShopper) {
        paramsAttachment.push(`userStatus=${selectedShopper}`);
      }

      // Add month and year from selectedDate
      if (selectedDate) {
        const month = selectedDate.month() + 1; // dayjs months are 0-indexed
        const year = selectedDate.year();
        paramsAttachment.push(`month=${month}`);
        paramsAttachment.push(`year=${year}`);
      }

      // Construct the query string
      const data =
        paramsAttachment.length > 0 ? `?${paramsAttachment.join("&")}` : "";

      // Replace with your actual API endpoint
      const response = await ApiGetShopperVenueMap(data);

      if (!response.status) {
        toast.error("Failed to load data");
      }

      //const apiData: ApiResponse = await response.json()
      let venueMarkers: MapMarker[] = [];
      if (response.status && response.data) {
        // Map venue data to markers
        if (response.data.venueMapDatas) {
          venueMarkers = response.data.venueMapDatas.map((venue, index) => ({
            id: venue.venueId,
            name: venue.venueName,
            lat: Number(venue.lat),
            lng: Number(venue.lng),
            type: venue.clientGroupName.toLowerCase(),
            logo: venue.logo,
            entityName: venue.entityName,
            clientGroupName: venue.clientGroupName,
            venuePriceDisplay: venue.venuePriceDisplay,
            index: index, // Add index to venue marker
          }));

          setMapMarkers(venueMarkers);
        }
        // Map user data to markers (for shoppers)
        if (response.data.userData) {
          const userMarkers: MapMarker[] =
            response.data.userData.status_filtered_users
              .filter((user) => user.user_latitude && user.user_longitude)
              .map((user, userIndex) => ({
                id: user.user_userId,
                name:
                  `${user.user_firstName} ${user.user_lastName}`.trim() ||
                  user.user_userName,
                lat: Number(user.user_latitude!),
                lng: Number(user.user_longitude!),
                type: user.userStatusName,
                logo: getShopperIcon(user.userStatusName, user.age),
                age: user.age,
                entityName: "Shopper",
                clientGroupName: user.userStatusName,
                dob: user.user_dob,
                emailId: user.user_emailId,
                contactNo: user.user_contactNo,
                index: venueMarkers.length + userIndex, // Offset to avoid overlap
              }));

          setShopperMarkers(userMarkers);
        }
      }
    } catch (error) {
      console.error("Error fetching map data:", error);
      toast.error("Error loading map data");
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch shopper details
  const fetchShopperDetails = async (shopperId: number) => {
    try {
      setLoadingShopperDetails(true);
      // Reset previous shopper details
      setShopperDetails(null);
      setShopperNotes1("");
      setTrainingNotes("");

      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1; // Months are zero-based, so add 1
      const currentYear = currentDate.getFullYear();

      const data = `?userId=${shopperId}&currentMonth=${currentMonth}&currentYear=${currentYear}`;

      // Call API to get shopper details
      const response = await ApiGetShopperPopUpDetails(data);

      if (!response.status) {
        toast.error("Failed to load shopper details");
        return;
      }

      // Set shopper details from API response
      setShopperDetails(response.data);

      // Set notes from API response
      if (response.data) {
        setShopperName(`${response.data.firstName} ${response.data.lastName}`)
        setShopperNotes1(response.data.shopperOtherDetail.shopperNotes || "");
      }
      if (response.data) {
        setTrainingNotes(response.data.shopperOtherDetail.trainingNotes || "");
      }
    } catch (error) {
      console.error("Error fetching shopper details:", error);
      toast.error("Error loading shopper details");
    } finally {
      setLoadingShopperDetails(false);
    }
  };

  // Function to fetch venue details
  const fetchVenueReportDetails = async (marker: MapMarker) => {
    try {
      setLoadingVenueDetails(true);
      // Reset previous venue details
      setVenueDetails(null);

      const data = `?venueId=${marker.id}&clientGroupName=${marker.clientGroupName}`;

      //Call API to get venue details - replace with your actual API function
      const response = await ApiGetVenuePreviousReports(data); // You'll need to import this

      if (!response.status) {
        toast.error("Failed to load venue details");
        return;
      }

      // Set venue details from API response
      setVenueDetails(response.data);
    } catch (error) {
      console.error("Error fetching venue details:", error);
      toast.error("Error loading venue details");
    } finally {
      setLoadingVenueDetails(false);
    }
  };
  console.log(shopperDetails);

  // Function to handle marker click
  const handleMarkerClick = (marker: MapMarker) => {
    setSelectedMarker(marker.index);

    // If it's a shopper marker, fetch shopper details
    if (marker.entityName === "Shopper") {
      fetchShopperDetails(marker.id);
    } else {
      fetchVenueReportDetails(marker);
    }
  };

  // Function to save shopper notes
  const saveShopperNotes = async (userId: any) => {
    try {
      const data = {
        userId: userId,
        shopperNotes: shopperNotes1,
      };
      const response = ApiPutShopperDetails(data);

      toast.success("Shopper notes saved successfully");
    } catch (error) {
      console.error("Error saving shopper notes:", error);
      toast.error("Error saving shopper notes");
    }
  };

  // Function to save training notes
  const saveTrainingNotes = async (userId: any) => {
    try {
      const data = {
        userId: userId,
        trainingNotes: trainingNotes,
      };
      const response = ApiPutShopperDetails(data);
      toast.success("Training notes saved successfully");
    } catch (error) {
      console.error("Error saving training notes:", error);
      toast.error("Error saving training notes");
    }
  };

  // Function to get shopper icon based on status and age
  const getShopperIcon = (status: string, age: string | null): string => {
    if (status === "active") {
      const ageNum = age ? Number.parseInt(age) : 0;
      if (ageNum >= 18 && ageNum <= 22) {
        return "https://mmfiles.s3.ap-southeast-2.amazonaws.com/ProjectFiles/PinBlue.png-04c76141-c916-46f3-a0b8-b9b6a57245d9"; // Blue pin
      } else {
        return "https://mmfiles.s3.ap-southeast-2.amazonaws.com/ProjectFiles/PinGreen.png-1339a1b4-3653-43ec-994f-c623c182e0b0"; // Pink pin
      }
    } else {
      return "https://mmfiles.s3.ap-southeast-2.amazonaws.com/ProjectFiles/PinRed.png-6c25052c-f163-4515-ad6c-bf5dae7c2fc3"; // Red pin
    }
  };

  const handleCheckboxChange =
    (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setCheckboxStates((prev) => ({
        ...prev,
        [name]: event.target.checked,
      }));
    };

  // Call API whenever checkbox states or selected states change
  useEffect(() => {
    const hasAnyCheckboxSelected = Object.values(checkboxStates).some(
      (value) => value
    );
    if (hasAnyCheckboxSelected || selectedState != "") {
      fetchMapData();
    } else {
      // Clear markers if no checkboxes are selected and no states selected
      setMapMarkers([]);
      setShopperMarkers([]);
    }
  }, [checkboxStates, selectedDate, selectedState]);

  // Filter markers based on selected types
  const getFilteredMarkers = (): MapMarker[] => {
    let filteredVenues: MapMarker[] = [];
    let filteredShoppers: MapMarker[] = [];

    // Filter venue markers
    if (checkboxStates.showAldi) {
      filteredVenues = [
        ...filteredVenues,
        ...mapMarkers.filter((m) => m.type === "aldi"),
      ];
    }
    if (checkboxStates.showBloom) {
      filteredVenues = [
        ...filteredVenues,
        ...mapMarkers.filter((m) => m.type === "blooms"),
      ];
    }
    if (checkboxStates.showMM) {
      filteredVenues = [
        ...filteredVenues,
        ...mapMarkers.filter((m) => m.type === "mm"),
      ];
    }

    // Filter shopper markers
    if (checkboxStates.shopperActive18) {
      filteredShoppers = [
        ...filteredShoppers,
        ...shopperMarkers.filter((m) => {
          const age = Number.parseInt(m.age) || 0;
          return m.type === "active" && age >= 18 && age <= 22;
        }),
      ];
    }
    if (checkboxStates.shopperActiveOther) {
      filteredShoppers = [
        ...filteredShoppers,
        ...shopperMarkers.filter((m) => {
          const age = Number.parseInt(m.age) || 0;
          return m.type === "active" && (age < 18 || age > 22);
        }),
      ];
    }
    if (checkboxStates.shopperInactive) {
      filteredShoppers = [
        ...filteredShoppers,
        ...shopperMarkers.filter((m) => m.type === "inActive"),
      ];
    }

    return [...filteredVenues, ...filteredShoppers];
  };

  if (loadError) {
    return <Typography color="error">Error loading Google Maps</Typography>;
  }

  if (!isLoaded) {
    return <Typography>Loading Google Maps...</Typography>;
  }

  const filteredMarkers = getFilteredMarkers();

  // Get the current selected marker object
  const currentMarker = filteredMarkers.find(
    (marker) => marker.index === selectedMarker
  );

  const getTimeSinceCreated = (createdOn: string): string => {
    if (!createdOn) return "N/A";

    const createdDate = new Date(createdOn);
    const now = new Date();

    const diffYears = now.getFullYear() - createdDate.getFullYear();
    const diffMonths =
      (now.getFullYear() - createdDate.getFullYear()) * 12 +
      (now.getMonth() - createdDate.getMonth());

    if (diffYears >= 1) {
      return `${diffYears} year${diffYears > 1 ? "s" : ""} ago`;
    } else if (diffMonths >= 1) {
      return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
    } else {
      return "Less than a month ago";
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };


  const handleOnFocus = (e) => {

    if (!staticLineInserted) {
      e.preventDefault(); // prevent newline from default action
      const now = new Date();
      const formatted = now.toLocaleString(); // e.g., 7/14/2025, 12:35:10 PM
      const updatedText = `${shopperNotes1.length > 1 ? `${shopperNotes1}\n` : ""}${ShopperName}-${formatted}:\n`; // add static line
      setShopperNotes1(updatedText);
      setStaticLineInserted(true);

      setTimeout(() => {
        const textarea = shoppernoteRef && shoppernoteRef.current;
        if (textarea) {
          textarea.focus(); // ensure focus
          textarea.setSelectionRange(updatedText.length, updatedText.length);
        }
      }, 0);

    }
  };
  const handleOnFocus2 = (e) => {
    if (!staticLineInserted2) {
      e.preventDefault(); // prevent newline from default action
      const now = new Date();
      const formatted = now.toLocaleString(); // e.g., 7/14/2025, 12:35:10 PM
      const updatedText = `${trainingNotes.length > 1 ? `${trainingNotes}\n` : ""}${ShopperName}-${formatted}:\n`; // add static line
      setTrainingNotes(updatedText);
      setStaticLineInserted2(true);

      setTimeout(() => {
        const textarea = trainingnoteRef && trainingnoteRef.current;
        if (textarea) {
          textarea.focus(); // ensure focus
          textarea.setSelectionRange(updatedText.length, updatedText.length);
        }
      }, 0);

    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box p={2}>
        {/* State Autocomplete Dropdown */}
        <Box mb={3}>
          <Box mb={3}>
            <Autocomplete
              options={stateOptions}
              value={
                stateOptions.find((state) => state.label === selectedState) ||
                null
              }
              getOptionLabel={(option) => option.label}
              onChange={(event, newValue) => {
                setSelectedState(newValue ? newValue.label : "");
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select State" fullWidth />
              )}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }}
            />
          </Box>
        </Box>

        {/* Filter Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={4} sx={{ justifyContent: "center" }}>
              {/* Left Column - Store Types */}
              <Grid item xs={12} lg={6}>
                <Box display="flex" flexDirection="column" gap={2}>
                  {/* Aldi Stores */}
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      <img
                        src="https://mmfiles.s3.ap-southeast-2.amazonaws.com/ShopperFiles/ProfilePictures/profilepic-1-image%20%281%29.png-2f692b6f-0d3e-49a9-b658-a70e98759edc"
                        alt="Aldi"
                        style={{ width: 30, height: 30 }}
                      />
                    </Avatar>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxStates.showAldi}
                          onChange={handleCheckboxChange("showAldi")}
                          sx={{ color: "var(--AutumnOlive) !important" }}
                        />
                      }
                      label={
                        <Typography sx={{ color: "#666", fontWeight: 500 }}>
                          Aldi Stores
                        </Typography>
                      }
                    />
                  </Box>

                  {/* Bloom Stores */}
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      <img
                        src="https://mmfiles.s3.ap-southeast-2.amazonaws.com/ShopperFiles/ProfilePictures/profilepic-1-image%20%282%29.png-6f906dfe-d33f-4d74-992e-b180c9f02fd8"
                        alt="Bloom"
                        style={{ width: 30, height: 30 }}
                      />
                    </Avatar>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxStates.showBloom}
                          onChange={handleCheckboxChange("showBloom")}
                          sx={{ color: "var(--AutumnOlive) !important" }}
                        />
                      }
                      label={
                        <Typography sx={{ color: "#666", fontWeight: 500 }}>
                          Bloom Stores
                        </Typography>
                      }
                    />
                  </Box>

                  {/* MM Stores */}
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      <img
                        src="https://mmfiles.s3.ap-southeast-2.amazonaws.com/ShopperFiles/ProfilePictures/profilepic-1-image.png-933eb71e-e9eb-404c-b8cc-322b4e0473d6"
                        alt="MM"
                        style={{ width: 30, height: 30 }}
                      />
                    </Avatar>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxStates.showMM}
                          onChange={handleCheckboxChange("showMM")}
                          sx={{ color: "var(--AutumnOlive) !important" }}
                        />
                      }
                      label={
                        <Typography sx={{ color: "#666", fontWeight: 500 }}>
                          MM Stores
                        </Typography>
                      }
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Right Column - Shopper Categories */}
              <Grid item xs={12} lg={6}>
                <Box display="flex" flexDirection="column" gap={2}>
                  {/* Shoppers - Active (18-22) */}
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      <img
                        src="https://mmfiles.s3.ap-southeast-2.amazonaws.com/ProjectFiles/PinBlue.png-04c76141-c916-46f3-a0b8-b9b6a57245d9"
                        alt="Shopper_active(18-22)"
                        style={{ width: 30, height: 30 }}
                      />
                    </Avatar>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxStates.shopperActive18}
                          onChange={handleCheckboxChange("shopperActive18")}
                          sx={{ color: "var(--AutumnOlive) !important" }}
                        />
                      }
                      label={
                        <Typography sx={{ color: "#666", fontWeight: 500 }}>
                          Shoppers - Active (18-22)
                        </Typography>
                      }
                    />
                  </Box>

                  {/* Shoppers - Active (Other Remaining) */}
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      <img
                        src="https://mmfiles.s3.ap-southeast-2.amazonaws.com/ProjectFiles/PinGreen.png-1339a1b4-3653-43ec-994f-c623c182e0b0"
                        alt="Shopper_active"
                        style={{ width: 30, height: 30 }}
                      />
                    </Avatar>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxStates.shopperActiveOther}
                          onChange={handleCheckboxChange("shopperActiveOther")}
                          sx={{ color: "var(--AutumnOlive) !important" }}
                        />
                      }
                      label={
                        <Typography sx={{ color: "#666", fontWeight: 500 }}>
                          Shoppers - Active (Other Remaining)
                        </Typography>
                      }
                    />
                  </Box>

                  {/* Shoppers - InActive */}
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      <img
                        src="https://mmfiles.s3.ap-southeast-2.amazonaws.com/ProjectFiles/PinRed.png-6c25052c-f163-4515-ad6c-bf5dae7c2fc3"
                        alt="Shopper_inActive"
                        style={{ width: 30, height: 30 }}
                      />
                    </Avatar>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxStates.shopperInactive}
                          onChange={handleCheckboxChange("shopperInactive")}
                          sx={{ color: "var(--AutumnOlive) !important" }}
                        />
                      }
                      label={
                        <Typography sx={{ color: "#666", fontWeight: 500 }}>
                          Shoppers - InActive
                        </Typography>
                      }
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Date Input */}
        <Box display="flex" justifyContent="center" mb={3}>
          <Grid item xs={12} sm={4}>
            <DatePicker
              label="Month/Year"
              views={["year", "month"]}
              value={selectedDate}
              onChange={setSelectedDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
        </Box>

        {/* Google Map */}
        <Box>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={4.5}
            options={{
              gestureHandling: "greedy", 
              scrollwheel:false,
              zoomControl:true
            }}
          >
            {filteredMarkers.map((marker) => (
              <Marker
                key={`${marker.entityName}-${marker.index}`}
                position={{ lat: marker.lat, lng: marker.lng }}
                onClick={() => handleMarkerClick(marker)}
                icon={{
                  url: marker.logo,
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
              >
                {selectedMarker === marker.index && (
                  <InfoBox
                    position={{ lat: marker.lat, lng: marker.lng }}
                    options={{
                      closeBoxURL: "",
                      enableEventPropagation: true,
                      pixelOffset: new window.google.maps.Size(0, -40),
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "white",
                        padding: "8px",
                        borderRadius: "8px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                        minWidth: "300px",
                        maxWidth: "400px",
                        maxHeight: "500px",
                        overflowY: "auto",
                        position: "relative",
                      }}
                    >
                      {/* Custom close button */}
                      <button
                        onClick={() => setSelectedMarker(null)}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          background: "transparent",
                          border: "none",
                          fontSize: "25px",
                          cursor: "pointer",
                          zIndex: 10,
                        }}
                      >
                        ✕
                      </button>

                      {/* Your existing content (Shopper or Venue) */}
                      {marker.entityName === "Shopper" ? (
                        // Shopper InfoWindow content - detailed design
                        <div style={{ fontFamily: "Arial, sans-serif" }}>
                          {loadingShopperDetails ? (
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              height="200px"
                            >
                              <CircularProgress size={40} />
                              <Typography sx={{ ml: 2 }}>
                                Loading shopper details...
                              </Typography>
                            </Box>
                          ) : (
                            <>
                              {/* Contact Details Section */}
                              <div
                                style={{
                                  backgroundColor: "#f5f5f5",
                                  padding: "8px",
                                  marginBottom: "12px",
                                  border: "1px solid #ccc",
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  sx={{ mb: 1 }}
                                >
                                  Contact Details:
                                </Typography>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "60px 1fr",
                                    gap: "4px",
                                    fontSize: "12px",
                                  }}
                                >
                                  <div style={{ fontWeight: "bold" }}>Age:</div>
                                  <div
                                    style={{
                                      border: "1px solid #ccc",
                                      padding: "2px 4px",
                                      backgroundColor: "white",
                                    }}
                                  >
                                    {shopperDetails?.contactDetails?.age ||
                                      currentMarker?.age ||
                                      "N/A"}
                                  </div>
                                  <div style={{ fontWeight: "bold" }}>
                                    Email:
                                  </div>
                                  <div
                                    style={{
                                      border: "1px solid #ccc",
                                      padding: "2px 4px",
                                      backgroundColor: "white",
                                    }}
                                  >
                                    {shopperDetails?.contactDetails?.email ||
                                      currentMarker?.emailId ||
                                      "N/A"}
                                  </div>
                                  <div style={{ fontWeight: "bold" }}>
                                    Phone:
                                  </div>
                                  <div
                                    style={{
                                      border: "1px solid #ccc",
                                      padding: "2px 4px",
                                      backgroundColor: "white",
                                    }}
                                  >
                                    {shopperDetails?.contactDetails?.phone ||
                                      currentMarker?.contactNo ||
                                      "N/A"}
                                  </div>
                                </div>
                              </div>

                              {/* This month allocations */}
                              <div style={{ marginBottom: "12px" }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  sx={{ mb: 1 }}
                                >
                                  This month allocations
                                </Typography>
                                <div
                                  style={{
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                      backgroundColor: "#8B9467",
                                      color: "white",
                                      fontSize: "11px",
                                      fontWeight: "bold",
                                      textAlign: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #fff",
                                      }}
                                    >
                                      MM
                                    </div>
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #fff",
                                      }}
                                    >
                                      BLOOMS
                                    </div>
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #fff",
                                      }}
                                    >
                                      ALDI
                                    </div>
                                    <div style={{ padding: "4px" }}>
                                      Transport
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                      backgroundColor: "white",
                                      fontSize: "11px",
                                      textAlign: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #ccc",
                                      }}
                                    >
                                      {shopperDetails
                                        ?.currentMonthAllocationCounts?.MM || 0}
                                    </div>
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #ccc",
                                      }}
                                    >
                                      {shopperDetails
                                        ?.currentMonthAllocationCounts
                                        ?.BLOOMS || 0}
                                    </div>
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #ccc",
                                      }}
                                    >
                                      {shopperDetails
                                        ?.currentMonthAllocationCounts?.ALDI ||
                                        0}
                                    </div>
                                    <div style={{ padding: "4px" }}>
                                      {shopperDetails
                                        ?.currentMonthAllocationCounts
                                        ?.TRANSPORT || 0}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Shopper History */}
                              <div style={{ marginBottom: "12px" }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  sx={{ mb: 1 }}
                                >
                                  Shopper History:
                                </Typography>
                                <div
                                  style={{
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      backgroundColor: "#f0f0f0",
                                      padding: "4px 8px",
                                      fontSize: "12px",
                                      fontWeight: "bold",
                                      textAlign: "center",
                                    }}
                                  >
                                    {shopperDetails?.totalReportCounts?.total ||
                                      0}{" "}
                                    Reports
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                      backgroundColor: "#8B9467",
                                      color: "white",
                                      fontSize: "11px",
                                      fontWeight: "bold",
                                      textAlign: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #fff",
                                      }}
                                    >
                                      ALDI
                                    </div>
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #fff",
                                      }}
                                    >
                                      Blooms
                                    </div>
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #fff",
                                      }}
                                    >
                                      MM
                                    </div>
                                    <div style={{ padding: "4px" }}>Beacon</div>
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                      backgroundColor: "white",
                                      fontSize: "11px",
                                      textAlign: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #ccc",
                                      }}
                                    >
                                      {shopperDetails?.totalReportCounts
                                        ?.ALDI || 0}
                                    </div>
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #ccc",
                                      }}
                                    >
                                      {shopperDetails?.totalReportCounts
                                        ?.BLOOMS || 0}
                                    </div>
                                    <div
                                      style={{
                                        padding: "4px",
                                        borderRight: "1px solid #ccc",
                                      }}
                                    >
                                      {shopperDetails?.totalReportCounts?.MM ||
                                        0}
                                    </div>
                                    <div style={{ padding: "4px" }}>
                                      {shopperDetails?.totalReportCounts
                                        ?.beacon || 0}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Stats Section and Editor's Comment */}
                              <div
                                style={{
                                  flex: "1",
                                  fontSize: "12px",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  marginBottom: "12px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                  }}
                                >
                                  <div
                                    style={{
                                      backgroundColor: "#8B9467",
                                      color: "white",
                                      padding: "4px",
                                      fontWeight: "bold",
                                      borderBottom: "1px solid #ccc",
                                      borderRight: "1px solid #ccc",
                                    }}
                                  >
                                    Member Since:
                                  </div>
                                  <div
                                    style={{
                                      padding: "4px",
                                      backgroundColor: "white",
                                      borderBottom: "1px solid #ccc",
                                    }}
                                  >
                                    {getTimeSinceCreated(
                                      shopperDetails?.createdOn
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      backgroundColor: "#8B9467",
                                      color: "white",
                                      padding: "4px",
                                      fontWeight: "bold",
                                      borderBottom: "1px solid #ccc",
                                      borderRight: "1px solid #ccc",
                                    }}
                                  >
                                    Last Report Rating:
                                  </div>
                                  <div
                                    style={{
                                      padding: "4px",
                                      backgroundColor: "white",
                                      borderBottom: "1px solid #ccc",
                                    }}
                                  >
                                    <div>
                                      {shopperDetails?.lastReportRatingData?.MM
                                        ?.reportCreatedDate
                                        ? `MM -- ${shopperDetails.lastReportRatingData
                                          .MM.reportMonth
                                        }/${shopperDetails.lastReportRatingData
                                          .MM.reportYear
                                        } -- ${formatDate(
                                          shopperDetails.lastReportRatingData
                                            .MM.submitDate
                                        )} -- ${shopperDetails.lastReportRatingData
                                          .MM.score
                                        }/100`
                                        : "MM -- N/A"}
                                    </div>
                                    <div>
                                      {shopperDetails?.lastReportRatingData
                                        ?.ALDI?.reportCreatedDate
                                        ? `ALDI -- ${shopperDetails.lastReportRatingData
                                          .ALDI.reportMonth
                                        }/${shopperDetails.lastReportRatingData
                                          .ALDI.reportYear
                                        } -- ${formatDate(
                                          shopperDetails.lastReportRatingData
                                            .ALDI.submitDate
                                        )} -- ${shopperDetails.lastReportRatingData
                                          .ALDI.score
                                        }/100`
                                        : "ALDI -- N/A"}
                                    </div>
                                    <div>
                                      {shopperDetails?.lastReportRatingData
                                        ?.BLOOMS?.reportCreatedDate
                                        ? `BLOOMS -- ${shopperDetails.lastReportRatingData
                                          .BLOOMS.reportMonth
                                        }/${shopperDetails.lastReportRatingData
                                          .BLOOMS.reportYear
                                        } -- ${formatDate(
                                          shopperDetails.lastReportRatingData
                                            .BLOOMS.submitDate
                                        )} -- ${shopperDetails.lastReportRatingData
                                          .BLOOMS.score
                                        }/100`
                                        : "BLOOMS -- N/A"}
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      backgroundColor: "#8B9467",
                                      color: "white",
                                      padding: "4px",
                                      fontWeight: "bold",
                                      borderBottom: "1px solid #ccc",
                                      borderRight: "1px solid #ccc",
                                    }}
                                  >
                                    Last Month:
                                  </div>
                                  <div
                                    style={{
                                      padding: "4px",
                                      backgroundColor: "white",
                                      borderBottom: "1px solid #ccc",
                                    }}
                                  >
                                    {`${shopperDetails?.totalReportCounts?.totalLastMonthReportCounts} Reports` ||
                                      "0 Reports"}
                                  </div>
                                  <div
                                    style={{
                                      backgroundColor: "#8B9467",
                                      color: "white",
                                      padding: "4px",
                                      fontWeight: "bold",
                                      borderBottom: "1px solid #ccc",
                                      borderRight: "1px solid #ccc",
                                    }}
                                  >
                                    Overall Rating:
                                  </div>
                                  <div
                                    style={{
                                      padding: "4px",
                                      backgroundColor: "white",
                                      borderBottom: "1px solid #ccc",
                                    }}
                                  >
                                    {shopperDetails?.shopperOverAllRating ||
                                      "N/A"}
                                  </div>
                                  <div
                                    style={{
                                      backgroundColor: "#8B9467",
                                      color: "white",
                                      padding: "4px",
                                      fontWeight: "bold",
                                      borderRight: "1px solid #ccc",
                                    }}
                                  >
                                    Editor's Comment:
                                  </div>
                                  <div
                                    style={{
                                      padding: "6px",
                                      backgroundColor: "white",
                                      fontSize: "11px",
                                      minHeight: "60px",
                                      overflowY: "auto",
                                    }}
                                  >
                                    {[
                                      shopperDetails?.lastReportRatingData?.MM,
                                      shopperDetails?.lastReportRatingData
                                        ?.ALDI,
                                      shopperDetails?.lastReportRatingData
                                        ?.BLOOMS,
                                    ]
                                      .filter((r) => r?.submitDate)
                                      .sort(
                                        (a, b) =>
                                          new Date(b.submitDate).getTime() -
                                          new Date(a.submitDate).getTime()
                                      )[0]?.editorComment ||
                                      "No comments available"}
                                  </div>
                                </div>
                              </div>

                              {/* Shopper Notes */}
                              <div
                                style={{
                                  marginBottom: "12px",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    backgroundColor: "#8B9467",
                                    color: "white",
                                    padding: "4px",
                                    fontWeight: "bold",
                                    fontSize: "12px",
                                  }}
                                >
                                  Shopper Notes
                                </div>
                                <div
                                  style={{
                                    padding: "6px",
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  <textarea
                                    ref={shoppernoteRef}
                                    value={shopperNotes1}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onFocus={(e) => { e.stopPropagation(); handleOnFocus(e) }}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) =>
                                      setShopperNotes1(e.target.value)
                                    }
                                    style={{
                                      width: "100%",
                                      border: "1px solid #ccc",
                                      padding: "6px",
                                      backgroundColor: "white",
                                      fontSize: "11px",
                                      minHeight: "40px",
                                      marginBottom: "4px",
                                      resize: "vertical",
                                      fontFamily: "Arial, sans-serif",
                                    }}
                                  />
                                  <button
                                    onClick={() =>
                                      saveShopperNotes(shopperDetails.userId)
                                    }
                                    style={{
                                      backgroundColor: "#8B9467",
                                      color: "white",
                                      border: "none",
                                      padding: "4px 12px",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      cursor: "pointer",
                                      width: "100%",
                                    }}
                                  >
                                    Save shopper notes
                                  </button>
                                </div>
                              </div>

                              {/* Training Notes */}
                              <div
                                style={{
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    backgroundColor: "#8B9467",
                                    color: "white",
                                    padding: "4px",
                                    fontWeight: "bold",
                                    fontSize: "12px",
                                  }}
                                >
                                  Training Notes
                                </div>
                                <div
                                  style={{
                                    padding: "6px",
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  <textarea
                                    value={trainingNotes}
                                    ref={trainingnoteRef}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onFocus={(e) => { e.stopPropagation(); handleOnFocus2(e) }}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) =>
                                      setTrainingNotes(e.target.value)
                                    }
                                    style={{
                                      width: "100%",
                                      border: "1px solid #ccc",
                                      padding: "6px",
                                      backgroundColor: "white",
                                      fontSize: "11px",
                                      minHeight: "40px",
                                      marginBottom: "6px",
                                      resize: "vertical",
                                      fontFamily: "Arial, sans-serif",
                                    }}
                                  />
                                  <button
                                    onClick={() =>
                                      saveTrainingNotes(shopperDetails.userId)
                                    }
                                    style={{
                                      backgroundColor: "#8B9467",
                                      color: "white",
                                      border: "none",
                                      padding: "4px 12px",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      cursor: "pointer",
                                      width: "100%",
                                    }}
                                  >
                                    Save Training Notes
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        // Venue InfoWindow content
                        <div>
                          {loadingVenueDetails ? (
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              height="200px"
                            >
                              <CircularProgress size={40} />
                              <Typography sx={{ ml: 2 }}>
                                Loading venue details...
                              </Typography>
                            </Box>
                          ) : (
                            <div>
                              <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ mb: 1 }}
                              >
                                {marker.name}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ mb: 2, fontWeight: 500 }}
                              >
                                Fees: {marker.venuePriceDisplay}
                              </Typography>

                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{ mb: 1 }}
                              >
                                Previous Reports:
                              </Typography>

                              <Box
                                sx={{
                                  border: "1px solid #ddd",
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  fontFamily: "Roboto, sans-serif", // Optional: consistent font
                                }}
                              >
                                {/* Table Header */}
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(5, 1fr)",
                                    backgroundColor: "#8B9467",
                                    color: "white",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    borderBottom: "1px solid #ddd",
                                  }}
                                >
                                  {[
                                    "Month",
                                    "Shopper",
                                    "Age",
                                    "Phone",
                                    "Score",
                                  ].map((header, i) => (
                                    <Box
                                      key={header}
                                      sx={{
                                        p: 1,
                                        borderRight:
                                          i < 4 ? "1px solid #fff" : "none",
                                      }}
                                    >
                                      {header}
                                    </Box>
                                  ))}
                                </Box>

                                {/* Table Rows */}
                                {venueDetails?.map((report, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: "repeat(5, 1fr)",
                                      backgroundColor:
                                        index % 2 === 0 ? "#f9f9f9" : "white",
                                      fontSize: "11px",
                                      borderBottom: "1px solid #ddd",
                                    }}
                                  >
                                    {[
                                      report.reportMonth,
                                      report.userName,
                                      report.age,
                                      report.contactNo,
                                      report.finalScore,
                                    ].map((val, i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          p: 1,
                                          borderRight:
                                            i < 4 ? "1px solid #ddd" : "none",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {val}
                                      </Box>
                                    ))}
                                  </Box>
                                ))}

                                {/* Optional: Fallback message */}
                                {(!venueDetails ||
                                  venueDetails.length === 0) && (
                                    <Box
                                      sx={{
                                        p: 2,
                                        textAlign: "center",
                                        fontSize: "12px",
                                        color: "#666",
                                      }}
                                    >
                                      No previous reports available
                                    </Box>
                                  )}
                              </Box>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </InfoBox>
                )}
              </Marker>
            ))}
          </GoogleMap>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ShopperVenueMap;
